import {
    Component,
    inject,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    LogicStepParameter,
    LogicStepParameterType,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import {
    ExtendedPrimitiveKinds,
    LookupKinds,
    SegmentedKinds,
} from '../../../utils/logic-step-parameter-kinds';
import {
    DependentParameterResponse,
    WorkflowParam,
} from '../../../utils/logic-step-parameter-types';
import { REFERENCE_SELECT_SOURCES } from '../../../utils/reference-select-source';
import { IWrappedWorkflowFormComponentProvider } from '../../../interfaces';
import { LogicstepLogicstepcodeParameterParametercodeApiService } from '@wdx/clmi/clmi-swagger-gen/api-services';
import { WorkflowDesignerFacade } from '../../../services';
import { switchMap, take, takeUntil } from 'rxjs';

@Component({
    selector: 'clmi-workflow-designer-parameter-group',
    templateUrl: './parameter-group.component.html',
    standalone: false,
})
export class WorkflowDesignerParameterGroupComponent
    extends WdxDestroyClass
    implements OnInit, OnChanges
{
    private logicStepParameterCachedApiService = inject(
        LogicstepLogicstepcodeParameterParametercodeApiService,
    );
    private workflowDesignerFacade = inject(WorkflowDesignerFacade);

    @Input() parameters: WorkflowParam[] = [];
    @Input() formGroup!: FormGroup;
    @Input() componentProvider?: IWrappedWorkflowFormComponentProvider;

    @Input() addCollectionItem!: (param: LogicStepParameter) => void;
    @Input() removeCollectionItem!: (paramCode: string, index: number) => void;

    readonly LogicStepParameterType = LogicStepParameterType;
    readonly PrimitiveKinds = ExtendedPrimitiveKinds;
    readonly LookupKinds = LookupKinds;
    readonly SegmentedKinds = SegmentedKinds;

    ngOnInit(): void {
        this.setupParameters();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['parameters'] || changes['formGroup']) {
            this.setupParameters();
        }
    }

    private setupParameters(): void {
        if (!this.formGroup || !this.parameters) {
            return;
        }

        this.parameters = this.cloneParameters(this.parameters);
        const parentCodes = new Set<string>();

        this.parameters.forEach((param) => {
            if (param.dependsOn?.length) {
                param.dependsOn.forEach((depCode) => {
                    parentCodes.add(depCode);
                    this.subscribeToParentControl(depCode);
                });
            }
        });

        parentCodes.forEach((parentCode) => {
            const control = this.formGroup.get(parentCode);
            if (!control) {
                return;
            }
            const currentValue = control.value;
            if (
                currentValue !== null &&
                currentValue !== undefined &&
                currentValue !== ''
            ) {
                this.handleDependentChange(parentCode, currentValue, false);
            }
        });
    }

    private cloneParameters(params: WorkflowParam[] = []): WorkflowParam[] {
        return params.map((param) => ({
            ...param,
            dependsOn: param.dependsOn ? [...param.dependsOn] : param.dependsOn,
            options: param.options
                ? param.options.map((opt) => ({ ...opt }))
                : param.options,
            properties: param.properties
                ? param.properties.map((prop) => ({ ...prop }))
                : param.properties,
        }));
    }

    private subscribeToParentControl(parentCode: string): void {
        const control = this.formGroup.get(parentCode);
        if (!control) {
            return;
        }

        control.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((value) => {
                if (value !== null && value !== undefined && value !== '') {
                    this.handleDependentChange(parentCode, value);
                }
            });
    }

    handleDependentChange(
        parentCode: string,
        value: unknown,
        resetValue = true,
    ) {
        this.parameters
            .filter((p) => p.dependsOn?.includes(parentCode))
            .forEach((depParam) => {
                const depCode = depParam.code;
                if (!depCode) {
                    return;
                }
                const control = this.formGroup.get(depCode);
                const existingValue = control?.value;
                if (control && resetValue) {
                    control.setValue(null);
                }
                this.workflowDesignerFacade.workflowContext$
                    .pipe(
                        take(1),
                        switchMap((ctx) => {
                            const node = ctx.node;

                            const payload = {
                                ...node.payload,
                                [parentCode]: value,
                                ...(resetValue
                                    ? { [depParam.code]: null }
                                    : {}),
                            };

                            return this.workflowDesignerFacade
                                .resolveEntityTypeForNode$(node)
                                .pipe(
                                    switchMap((targetEntityType) =>
                                        this.logicStepParameterCachedApiService.post(
                                            node.code,
                                            parentCode,
                                            payload,
                                            targetEntityType,
                                        ),
                                    ),
                                );
                        }),
                    )
                    .subscribe((response: DependentParameterResponse | null) => {
                        if (!response) {
                            return;
                        }

                        if (response.applicableOperators?.length) {
                            this.applyOperatorOptions(
                                response.applicableOperators,
                            );
                        }

                        const shouldRecreateControl =
                            depCode !== response.code ||
                            depParam.kind !== response.kind ||
                            (!!response.options &&
                                response.options.length !==
                                    (depParam.options?.length ?? 0));

                        if (shouldRecreateControl) {
                            const existingValue =
                                this.formGroup.get(depCode)?.value;

                            this.formGroup.removeControl(depCode, {
                                emitEvent: false,
                            });
                            this.formGroup.addControl(
                                depCode,
                                new FormControl(null),
                                { emitEvent: false },
                            );
                            if (!resetValue && existingValue !== null) {
                                this.formGroup
                                    .get(depCode)
                                    ?.setValue(existingValue, {
                                        emitEvent: false,
                                    });
                            }
                        } else if (resetValue) {
                            this.formGroup.get(depCode)?.setValue(null);
                        }

                        const idx = this.parameters.findIndex(
                            (p) => p.code === depCode,
                        );

                        if (idx !== -1) {
                            const newParam: WorkflowParam = {
                                ...depParam,
                                code: depCode,
                                kind: response.kind,
                                options: response.options?.length
                                    ? response.options.map((o) => ({
                                          label: o.label,
                                          value: o.value,
                                      }))
                                    : undefined,
                                properties: [],
                            };

                            const selectSource = response.referenceType
                                ? REFERENCE_SELECT_SOURCES[
                                      response.referenceType
                                  ]
                                : undefined;

                            if (selectSource) {
                                newParam.kind = LogicStepParameterType.Select;
                                newParam.selectApiSource = selectSource;
                            } else if (response.referenceType) {
                                newParam.properties = [
                                    {
                                        type: 'SystemEntity',
                                        options: [
                                            {
                                                value: response.referenceType,
                                            },
                                        ],
                                    },
                                ];
                            }

                            this.parameters[idx] = newParam;
                            this.parameters = [...this.parameters];

                            const depControl = this.formGroup.get(depCode);
                            if (depControl) {
                                if (resetValue) {
                                    depControl.setValue(null);
                                } else if (
                                    existingValue !== null &&
                                    existingValue !== undefined
                                ) {
                                    depControl.setValue(existingValue, {
                                        emitEvent: false,
                                    });
                                }
                            }
                        }
                    });
            });
    }

    private applyOperatorOptions(operators: string[]) {
        const idx = this.parameters.findIndex((p) => p.code === 'Operator');
        if (idx === -1) {
            return;
        }

        const existingOptions = this.parameters[idx].options ?? [];
        const labelByValue = new Map(
            existingOptions.map((opt) => [opt.value, opt.label]),
        );
        const operatorOptions = operators.map((op) => ({
            label: labelByValue.get(op) ?? op,
            value: op,
        }));

        this.parameters[idx] = {
            ...this.parameters[idx],
            options: operatorOptions,
        };
        this.parameters = [...this.parameters];
    }

    isParameterVisible(param: WorkflowParam): boolean {
        if (!param.dependsOn || param.dependsOn.length === 0) {
            return true;
        }

        return param.dependsOn.every((depCode: string) => {
            const control = this.formGroup.get(depCode);
            return (
                control &&
                control.value !== null &&
                control.value !== undefined &&
                control.value !== ''
            );
        });
    }
}
